import { useEffect } from 'react'
import { useEconomyStore } from '@/state/useEconomyStore'
import { TradingPostPanel } from './panels/TradingPostPanel'
import { HousePanel } from './panels/HousePanel'
import { CastlePanel } from './panels/CastlePanel'
import { ChurchPanel } from './panels/ChurchPanel'
import { PubPanel } from './panels/PubPanel'
import { StablePanel } from './panels/StablePanel'
import { BarnPanel } from './panels/BarnPanel'
import { DockPanel } from './panels/DockPanel'
import { FarmPanel } from './panels/FarmPanel'
import { PasturePanel } from './panels/PasturePanel'
import { ArmoryPanel } from './panels/ArmoryPanel'
import { getWallet, registerPlayer, getMarketPrices } from '@/economy/api'
import { PROFILE_DISPLAY_NAMES } from '@/economy/types'
import './BuildingPanel.css'

const PANEL_TITLES: Record<string, string> = {
  castle: 'KEEP',
  house: "VILLAGER'S HOME",
  trading_post: 'VILLAGE MARKET',
  church: 'CHAPEL',
  pub: 'TAVERN',
  armory: 'ARMORY',
  stable: 'STABLE',
  barn: 'STOREHOUSE',
  dock: 'HARBOR',
  farm: 'FARMSTEAD',
  pasture: 'PADDOCK',
}

export function BuildingPanel() {
  const interaction = useEconomyStore((s) => s.activeInteraction)
  const panelOpen = useEconomyStore((s) => s.panelOpen)
  const closePanel = useEconomyStore((s) => s.closePanel)
  const wallet = useEconomyStore((s) => s.wallet)
  const setWallet = useEconomyStore((s) => s.setWallet)
  const setWalletLoading = useEconomyStore((s) => s.setWalletLoading)
  const setMarketPrices = useEconomyStore((s) => s.setMarketPrices)

  // Fetch wallet and market data when panel opens
  useEffect(() => {
    if (!panelOpen || !interaction) return

    // Fetch wallet
    async function loadWallet() {
      setWalletLoading(true)
      try {
        const w = await getWallet()
        setWallet(w)
      } catch {
        // Try registering first if wallet doesn't exist
        try {
          const w = await registerPlayer()
          setWallet(w)
        } catch { /* ignore — player may not be logged in */ }
      }
      setWalletLoading(false)
    }
    loadWallet()

    // Fetch market prices if this village has a server market
    if (interaction.serverId !== null) {
      getMarketPrices(interaction.serverId)
        .then((prices) => setMarketPrices(interaction.villageIndex, prices))
        .catch(() => { /* market fetch failed silently */ })
    }
  }, [panelOpen, interaction, setWallet, setWalletLoading, setMarketPrices])

  if (!panelOpen || !interaction) return null

  const title = PANEL_TITLES[interaction.structureType] || interaction.structureType.toUpperCase()
  const villageSub = interaction.profileType
    ? `${PROFILE_DISPLAY_NAMES[interaction.profileType]} — ${interaction.villageName}`
    : interaction.villageName

  const PanelContent = getPanelComponent(interaction.structureType)

  return (
    <div className="building-panel-overlay" onClick={closePanel}>
      <div className="building-panel" onClick={(e) => e.stopPropagation()}>
        <header className="building-panel__header">
          <div>
            <h2 className="building-panel__title">{title}</h2>
            <p className="building-panel__village">{villageSub}</p>
          </div>
          <button className="building-panel__close" onClick={closePanel}>✕</button>
        </header>

        <div className="building-panel__content">
          <PanelContent interaction={interaction} wallet={wallet} />
        </div>

        {wallet && (
          <footer className="building-panel__footer">
            <span className="building-panel__wallet">
              {wallet.solidus > 0 && <span className="wallet-solidus">{wallet.solidus}s</span>}
              <span className="wallet-follis">{wallet.follis}ƒ</span>
            </span>
            <span className="building-panel__cargo">
              Cargo: {wallet.cargo.length > 0
                ? wallet.cargo.map(([item, qty]) => `${qty}×${item.replace(/_/g, ' ')}`).join(', ')
                : 'Empty'
              }
            </span>
          </footer>
        )}
      </div>
    </div>
  )
}

function getPanelComponent(type: string) {
  switch (type) {
    case 'trading_post': return TradingPostPanel
    case 'house': return HousePanel
    case 'armory': return ArmoryPanel
    case 'castle': return CastlePanel
    case 'church': return ChurchPanel
    case 'pub': return PubPanel
    case 'stable': return StablePanel
    case 'barn': return BarnPanel
    case 'dock': return DockPanel
    case 'farm': return FarmPanel
    case 'pasture': return PasturePanel
    default: return DefaultPanel
  }
}

function DefaultPanel({ interaction }: { interaction: any; wallet: any }) {
  return <p className="panel-placeholder">No interaction available for {interaction.structureType}.</p>
}
